import SelectionFormField from 'components/form/SelectionFormField';
import TextFormField from 'components/form/TextFormField';
import { useFormik } from 'formik';
import { Box, Button } from 'grommet';
import AssetImageEditor from 'modules/admin/components/AssetImageEditor';
import MarkdownEditor from 'modules/admin/components/MarkdownEditor';
import { actions } from 'modules/asset/asset$';
import { colors } from 'modules/root/theme';
import React, { useEffect } from 'react';
import { dispatchAction } from 'rxbeach';
import { Asset, BaseAsset } from 'types';
import { number, object, string } from 'yup';

interface PageEditorProps {
  asset: Asset | BaseAsset;
  onDelete: () => void;
}

const validationSchema = object({
  label: string().required('Må ha med tittel'),
  markdown: string().required('Må ha med tekst'),
  type: string().required(),
  image: string().nullable(),
  order: number().optional(),
  tab: string().optional(),
});

const isNew = (test: any): test is BaseAsset[] => !test.id;

const initialValues = {
  label: '',
  type: '',
  tab: '',
  markdown: '',
  shortid: '',
  order: '1',
}

const PageEditor = ({ asset, onDelete }: PageEditorProps) => {
  const {
    touched,
    values,
    errors,
    handleChange,
    handleBlur,
    setValues,
    submitForm,
    setFieldValue,
  } = useFormik({
    initialValues: { ...initialValues, ...asset },
    validationSchema,
    onSubmit: (data) => {
      if (isNew(data)) dispatchAction(actions.apiCreate([data]));
      else dispatchAction(actions.apiPut([data as Asset]));
    },
  });

  useEffect(() => {
    setValues({ ...initialValues, ...asset });
  }, [JSON.stringify(asset)]);

  const formProps = { touched, errors, values, handleChange, handleBlur, setValues };

  return (
    <>
      <Box direction="row" style={{ minHeight: '40px', height: "40px" }}>
        {/* TODO NOT SHOW OPPRETT BUTTON AFTER CREATION */}
        <Button
          size="small"
          primary
          label={isNew(values) ? 'Opprett' : 'Lagre'}
          disabled={Object.keys(errors).length !== 0}
          onClick={() => submitForm()}
        />
        {!isNew(values) && (
          <Button
            color={colors.danger.background}
            label="Slett"
            size="small"
            secondary
            onClick={() => {
              dispatchAction(actions.apiDelete([(asset as Asset).id]))
              onDelete()
            }}
          />
        )}
      </Box>
      <Box pad="medium" height="medium">
        <Box justify="start" pad="small" direction="row" gap="small">
          <Box width="medium">
            <TextFormField {...formProps} label="Tittel" id="label" />
            <TextFormField {...formProps} label="Shortid" id="shortid" />
            <TextFormField {...formProps} label="Rekkefølge" id="order" />
          </Box>
          <Box width="medium">
            <TextFormField {...formProps} label="Tab" id="tab" />
            <SelectionFormField
              {...formProps}
              label="Type"
              id="type"
              options={['kurs', 'seiletilbud', 'info', 'blimedlem', 'leie']}
            />
          </Box>
          <AssetImageEditor values={values} setFieldValue={setFieldValue} />
        </Box>
      </Box>
      <Box pad="medium" height="medium">
        <MarkdownEditor
          markdown={values.markdown}
          setMarkdown={(value: string) => setFieldValue('markdown', value)}
        />
      </Box>
    </>
  );
};

export default PageEditor;
